import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { includes, keys, reject, without } from 'ramda'
import { Box, IconClose, Text } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { ScrollContainer } from 'react-indiana-drag-scroll'

import { breakpoints, colors } from '@/styles'
import useFilterLabels from '@/features/Listings/hooks/use-filter-labels'

import 'react-indiana-drag-scroll/dist/style.css'

import { updatePropertyFilters } from '../utils'

const Wrapper = styled.div`
  margin-top: 12px;
  width: 100%;

  ${props =>
    props.isMapMode &&
    `
    width: calc(100% + 40px);
    margin: 12px -20px 0;
    @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
      width: 100%;
      height: ${props.isUniversityPage ? '40px' : 'auto'};
      margin: 0;
      background-color: ${colors.white};
    }
  `}
`

const ScrollWrapper = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;

  ${props =>
    props.isMapMode &&
    `
    padding: 0 20px;
    @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
      padding: ${props.isUniversityPage ? '8px 16px' : '0 16px 8px'};
    }
  `}
`

const RemoveButton = styled.button`
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;

  svg {
    pointer-events: none;
  }
`

const Spacer = styled.div`
  height: 24px;
  padding: 0 4px;
`

const IndicatorItem = ({ id, label, handleRemove }) => {
  const { t } = useTranslation()
  return (
    <Box
      display="flex"
      alignItems="center"
      paddingLeft="xxsmall"
      borderRadius="full"
      boxShadow="borderNeutralLight"
      style={{ flexShrink: 0, background: vars.backgroundColor.navItem }}
    >
      <Text size="xsmall">{label}</Text>
      <RemoveButton
        aria-label={t(
          'filters:b.listing_filter.edit.remove_filter_label.button',
          {
            defaultValue: 'Remove filter {{ label }}',
            label
          }
        )}
        id={`remove-filter-${id}`}
        onClick={() => handleRemove(id)}
      >
        <Text size="xsmall">
          <IconClose />
        </Text>
      </RemoveButton>
    </Box>
  )
}

const ActiveFilterIndicators = ({
  filters,
  onChange,
  isMapMode,
  isUniversityPage
}) => {
  const [
    getFilterLabels,
    ,
    {
      HOUSING_TYPES,
      UNIT_TYPES,
      PETS,
      RENTAL_TYPES,
      FEATURES,
      UTILITIES,
      DEALS
    }
  ] = useFilterLabels()

  const handleRemove = id => {
    if (includes(id, keys({ ...HOUSING_TYPES, ...UNIT_TYPES }))) {
      return updatePropertyFilters(
        id,
        filters.bedroom_count,
        filters.housing_types,
        filters.unit_types,
        onChange
      )
    }
    if (includes(id, keys(PETS))) {
      return onChange({
        pet_policy: without([id], filters.pet_policy)
      })
    }
    if (includes(id, keys(RENTAL_TYPES))) {
      return onChange({
        rental_type: without([id], filters.rental_type)
      })
    }
    if (includes(id, keys(FEATURES))) {
      return onChange({
        features: without([id], filters.features)
      })
    }
    if (includes(id, keys(UTILITIES))) {
      return onChange({
        utilities: without([id], filters.utilities)
      })
    }
    if (includes(id, keys(DEALS))) {
      return onChange({
        deals: without([id], filters.deals)
      })
    }
    switch (id) {
      case 'price':
        onChange({
          min_price: null,
          max_price: null
        })
        break
      case 'availability':
        onChange({
          availability_date: null,
          custom_availability_date: null
        })
        break
      case 'distance':
        onChange({
          distance: null,
          location: null
        })
        break
      default:
        onChange({
          [id]: null
        })
    }
  }

  const filterList = getFilterLabels(filters)

  // University pages have some additional filters to display
  const rejectFilters = isUniversityPage ? ['search_only_verified'] : []

  const filteredFilterList = reject(
    f => includes(f.id, ['sort', ...rejectFilters]),
    filterList
  )

  if (!filteredFilterList.length) return null

  return (
    <Wrapper isMapMode={isMapMode} isUniversityPage={isUniversityPage}>
      <ScrollContainer>
        <ScrollWrapper
          isMapMode={isMapMode}
          isUniversityPage={isUniversityPage}
        >
          {filteredFilterList.map(filter => (
            <IndicatorItem
              key={filter.id}
              id={filter.id}
              label={filter.label}
              handleRemove={handleRemove}
            />
          ))}
          <Spacer>&nbsp;</Spacer>
        </ScrollWrapper>
      </ScrollContainer>
    </Wrapper>
  )
}

export default ActiveFilterIndicators
